import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useRef, useState, useEffect, useCallback } from 'react';
import { Wrapper, CurrentValueWrapper, ControlLine, ControlLineWrapper, ControlLineBackground, Handle, MinValueLabel, MaxValueLabel, CurrentValue } from './style.js';
import getCheckedSiteId from '../../utils/siteId.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

var _excluded = ["uniqueId", "className", "min", "max", "separationLabel", "step", "separationLimit", "onChangeComplete", "hidden", "format", "siteId"];
var listen = function listen(node, event, handler) {
  node.addEventListener(event, handler);
  return {
    dispose: function dispose() {
      node.removeEventListener(event, handler);
    }
  };
};
var clamp = function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
};
var getValueFromPosition = function getValueFromPosition(pos, min, max, limit, step) {
  var percentage = clamp(pos, 0, limit) / (limit || 1);
  var baseVal = step * Math.round(percentage * (max - min) / step);
  var value = baseVal + min;
  if (value >= max) {
    value = max;
  }
  if (value <= min) {
    value = min;
  }
  return value;
};
var getPositionFromValue = function getPositionFromValue(value, limit, min, max) {
  var diffMaxMin = max - min;
  var diffValMin = value - min;
  var percentage = diffValMin / diffMaxMin;
  var pos = Math.round(percentage * limit);
  return pos || 0;
};
var getCoordinates = function getCoordinates(pos, grab, min, max, limit, step) {
  var value = getValueFromPosition(pos, min, max, limit, step);
  var handlePos = getPositionFromValue(value, limit, min, max);
  return {
    fill: handlePos + grab,
    handle: handlePos,
    label: handlePos
  };
};
var formatValue = function formatValue(value, format, siteId) {
  return format ? format(value, siteId) : value;
};
var getPosition = function getPosition(e, grab, sliderRef, min, max, limit, step) {
  var coordinate = !e.touches ? e.clientX : e.touches[0].clientX;
  var direction = sliderRef.current.getBoundingClientRect().left;
  var pos = coordinate - direction - grab;
  var value = getValueFromPosition(pos, min, max, limit, step);
  return value;
};
var handleSliderClick = function handleSliderClick(e, sliderRef, min, max, limit, lowValue, highValue, setLowValue, setHighValue, step, grab, separationLimit) {
  var clickValue = getPosition(e, grab, sliderRef, min, max, limit, step);
  var clickPosition = getPositionFromValue(clickValue, limit, min, max);
  var lowPosition = getPositionFromValue(lowValue, limit, min, max);
  var highPosition = getPositionFromValue(highValue, limit, min, max);
  if (clickPosition < lowPosition) {
    setLowValue(clickValue);
  } else if (clickPosition > highPosition) {
    setHighValue(clickValue);
  } else {
    var maxLowPosition = lowPosition + Math.floor((highPosition - lowPosition) / 2);
    if (clickPosition <= maxLowPosition) {
      var value = highValue - clickValue < separationLimit ? highValue - separationLimit : clickValue;
      setLowValue(value);
    } else {
      var _value = clickValue - lowValue < separationLimit ? lowValue + separationLimit : clickValue;
      setHighValue(_value);
    }
  }
};
var handleEnd = function handleEnd(lowValue, highValue, onChangeComplete, listeners) {
  if (onChangeComplete) {
    onChangeComplete({
      lowValue: lowValue,
      highValue: highValue
    });
  }
  var _listeners$current = listeners.current,
    mouseMove = _listeners$current.mouseMove,
    mouseUp = _listeners$current.mouseUp;
  if (mouseMove) {
    mouseMove.dispose();
  }
  if (mouseUp) {
    mouseUp.dispose();
  }
};
var handleNoop = function handleNoop(e) {
  e.stopPropagation();
  if (e instanceof MouseEvent) {
    e.preventDefault();
  }
};
var handleLowDrag = function handleLowDrag(e, separationLimit, highValue, setValue, grab, sliderRef, min, max, limit, step) {
  handleNoop(e);
  var separation = separationLimit || 0.2;
  var lowValue = getPosition(e, grab, sliderRef, min, max, limit, step);
  if (lowValue > highValue - separation) {
    lowValue = highValue - separation;
  }
  // TODO: We can probably avoid re-render by just working directly on the node we drag
  //       and update the limit string (or just reduce the workload inside render if we are in drag-mode)
  setValue(lowValue);
};
var handleHighDrag = function handleHighDrag(e, separationLimit, lowValue, setValue, grab, sliderRef, min, max, limit, step) {
  handleNoop(e);
  var highValue = getPosition(e, grab, sliderRef, min, max, limit, step);
  if (highValue < lowValue + separationLimit) {
    highValue = lowValue + separationLimit;
  }
  setValue(highValue);
};
var DoubleSlider = function DoubleSlider(_ref) {
  var uniqueId = _ref.uniqueId,
    className = _ref.className,
    min = _ref.min,
    max = _ref.max,
    separationLabel = _ref.separationLabel,
    step = _ref.step,
    separationLimit = _ref.separationLimit,
    onChangeComplete = _ref.onChangeComplete,
    hidden = _ref.hidden,
    format = _ref.format,
    siteId = _ref.siteId,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var sliderRef = useRef(null);
  var handleLowRef = useRef(null);
  var handleHighRef = useRef(null);
  var listeners = useRef({
    mouseMove: null,
    mouseUp: null
  });
  var _useState = useState(otherProps.lowValue),
    _useState2 = _slicedToArray(_useState, 2),
    lowValue = _useState2[0],
    setLowValue = _useState2[1];
  var _useState3 = useState(otherProps.highValue),
    _useState4 = _slicedToArray(_useState3, 2),
    highValue = _useState4[0],
    setHighValue = _useState4[1];
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    limit = _useState6[0],
    setLimit = _useState6[1];
  var _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    grab = _useState8[0],
    setGrab = _useState8[1];
  var lowValueRef = useRef(otherProps.lowValue);
  var highValueRef = useRef(otherProps.highValue);
  var lowPosition = getPositionFromValue(lowValue, limit, min, max);
  var highPosition = getPositionFromValue(highValue, limit, min, max);
  var checkedSiteId = getCheckedSiteId(siteId);
  var setLowValueStateAndRef = function setLowValueStateAndRef(newLowValue) {
    setLowValue(newLowValue);
    lowValueRef.current = newLowValue;
  };
  var setHighValueStateAndRef = function setHighValueStateAndRef(newHighValue) {
    setHighValue(newHighValue);
    highValueRef.current = newHighValue;
  };
  useEffect(function () {
    setLowValueStateAndRef(otherProps.lowValue);
  }, [otherProps.lowValue]);
  useEffect(function () {
    setHighValueStateAndRef(otherProps.highValue);
  }, [otherProps.highValue]);
  var memoizedhandleSliderClick = useCallback(function (e) {
    handleSliderClick(e, sliderRef, min, max, limit, lowValue, highValue, setLowValueStateAndRef, setHighValueStateAndRef, step, grab, separationLimit);
  }, [sliderRef, min, max, limit, step, limit, lowValue, highValue, setLowValueStateAndRef, setHighValueStateAndRef, step, grab, separationLimit]);
  var memoizedHandleEnd = useCallback(function () {
    handleEnd(lowValueRef.current, highValueRef.current, onChangeComplete, listeners);
  }, [lowValueRef.current, highValueRef.current, onChangeComplete, listeners]);
  var memoizedHandleLow = useCallback(function (e) {
    handleLowDrag(e, separationLimit, highValue, setLowValueStateAndRef, grab, sliderRef, min, max, limit, step);
  }, [separationLimit, highValue, setLowValueStateAndRef, grab, sliderRef]);
  var memoizedHandleHigh = useCallback(function (e) {
    handleHighDrag(e, separationLimit, lowValue, setHighValueStateAndRef, grab, sliderRef, min, max, limit, step);
  }, [separationLimit, lowValue, setHighValueStateAndRef, grab, sliderRef, min, max, limit, step]);
  var memoizedHandleStartLow = useCallback(function () {
    listeners.current = {
      mouseMove: listen(document, 'mousemove', memoizedHandleLow),
      mouseUp: listen(document, 'mouseup', memoizedHandleEnd)
    };
  }, [listeners.current, memoizedHandleLow, memoizedHandleEnd]);
  var memoizedHandleStartHigh = useCallback(function () {
    listeners.current = {
      mouseMove: listen(document, 'mousemove', memoizedHandleHigh),
      mouseUp: listen(document, 'mouseup', memoizedHandleEnd)
    };
  }, [listeners.current, memoizedHandleHigh, memoizedHandleEnd]);

  // Handle resize
  useEffect(function () {
    var handleUpdate = function handleUpdate() {
      var sliderPos = sliderRef && (sliderRef.current.offsetWidth || 0);
      var handlePos = handleLowRef && (handleLowRef.current.offsetWidth || 0);
      if (hidden && !sliderPos) {
        return;
      }
      setLimit(sliderPos - handlePos);
      setGrab(handlePos / 2);
    };
    var resizeListener = listen(window, 'resize', handleUpdate);
    handleUpdate();
    return function () {
      resizeListener.dispose();
    };
  }, [hidden, handleLowRef.current, sliderRef.current]);
  var delta = (+max || 0) - (+min || 0);
  var isInactive = otherProps.isInactive || delta < 0.3;
  var lowCoords = getCoordinates(lowPosition, grab, min, max, limit, step);
  var highCoords = getCoordinates(highPosition, grab, min, max, limit, step);
  var fillStyle = {
    left: "".concat(lowCoords.fill, "px"),
    width: "".concat(highCoords.fill - lowCoords.fill, "px")
  };
  var lowHandleStyle = {
    left: "".concat(lowCoords.handle, "px"),
    touchAction: 'none'
  };
  var highHandleStyle = {
    left: "".concat(highCoords.handle, "px"),
    touchAction: 'none'
  };
  var formattedLowValue = formatValue(lowValue, format, checkedSiteId);
  var formattedHighValue = formatValue(highValue, format, checkedSiteId);
  if (isInactive) {
    lowHandleStyle.cursor = 'default';
    highHandleStyle.cursor = 'default';
    var minValueToDisplay = min === 0 ? '' : formatValue(min, format, checkedSiteId);
    var maxValueToDisplay = min === max ? '' : formatValue(max, format, checkedSiteId);
    return /*#__PURE__*/jsxs(Wrapper, {
      id: uniqueId,
      children: [/*#__PURE__*/jsx(CurrentValueWrapper, {
        children: "\xA0"
      }), /*#__PURE__*/jsxs(ControlLine, {
        ref: sliderRef,
        className: className,
        children: [/*#__PURE__*/jsx("div", {
          style: fillStyle
        }), /*#__PURE__*/jsx("div", {
          ref: handleLowRef,
          style: lowHandleStyle
        }), /*#__PURE__*/jsx("div", {
          ref: handleHighRef,
          style: highHandleStyle
        })]
      }), /*#__PURE__*/jsxs("div", {
        children: [/*#__PURE__*/jsx("div", {
          children: minValueToDisplay
        }), /*#__PURE__*/jsx("div", {
          children: maxValueToDisplay
        })]
      })]
    });
  }
  var currentValueElement = /*#__PURE__*/jsx(CurrentValueWrapper, {
    children: lowValue !== min || highValue !== max ? /*#__PURE__*/jsxs(Fragment, {
      children: [formattedLowValue && /*#__PURE__*/jsx(CurrentValue, {
        children: formattedLowValue
      }), separationLabel && /*#__PURE__*/jsx(CurrentValue, {
        children: separationLabel
      }), formattedHighValue && /*#__PURE__*/jsx(CurrentValue, {
        children: formattedHighValue
      })]
    }) : /*#__PURE__*/jsx(CurrentValue, {
      children: "\xA0"
    })
  });
  return /*#__PURE__*/jsxs(Wrapper, {
    id: uniqueId,
    children: [currentValueElement, /*#__PURE__*/jsx(ControlLineWrapper, {
      ref: sliderRef,
      className: className,
      onMouseDown: memoizedhandleSliderClick,
      onTouchStart: memoizedhandleSliderClick,
      onMouseUp: memoizedHandleEnd,
      onTouchEnd: memoizedHandleEnd,
      children: /*#__PURE__*/jsxs(ControlLineBackground, {
        className: className,
        children: [/*#__PURE__*/jsx(ControlLine, {
          style: fillStyle
        }), /*#__PURE__*/jsx(Handle, {
          ref: handleLowRef,
          onMouseDown: memoizedHandleStartLow,
          onTouchMove: memoizedHandleLow,
          onTouchEnd: memoizedHandleEnd,
          style: lowHandleStyle
        }), /*#__PURE__*/jsx(Handle, {
          ref: handleHighRef,
          onMouseDown: memoizedHandleStartHigh,
          onTouchMove: memoizedHandleHigh,
          onTouchEnd: memoizedHandleEnd,
          style: highHandleStyle
        })]
      })
    }), /*#__PURE__*/jsxs("div", {
      children: [/*#__PURE__*/jsx(MinValueLabel, {
        children: formatValue(min, format, checkedSiteId)
      }), /*#__PURE__*/jsx(MaxValueLabel, {
        children: formatValue(max, format, checkedSiteId)
      })]
    })]
  });
  /* eslint-enable jsx-a11y/no-static-element-interactions */
};

DoubleSlider.defaultProps = {
  min: 0,
  max: 100,
  step: 1,
  lowValue: 0,
  highValue: 100,
  siteId: 1,
  separationLimit: 2,
  hidden: false,
  format: function format() {},
  onChangeComplete: function onChangeComplete() {},
  uniqueId: '',
  className: '',
  isInactive: false,
  separationLabel: ''
};
var BaseDoubleSlider = DoubleSlider;

export { BaseDoubleSlider as default };
//# sourceMappingURL=_DoubleSlider.js.map
